import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserCircle, Phone, Clock, Calendar, X, Scissors } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { getPeluqueros, createPeluquero, updatePeluquero, deletePeluquero, getServicios } from "@/lib/api";
import { format } from "date-fns";
import { es } from "date-fns/locale";

const DIAS = ["Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado", "Domingo"];
const COLORES = ["#3A5A40", "#5D4E37", "#4A6FA5", "#8B4513", "#2F4F4F", "#6B4423", "#7B3F00", "#1E3A5F"];

function createDefaultHorarios() {
  return [
    { dia: 0, inicio: "09:00", fin: "20:00", activo: true },
    { dia: 1, inicio: "09:00", fin: "20:00", activo: true },
    { dia: 2, inicio: "09:00", fin: "20:00", activo: true },
    { dia: 3, inicio: "09:00", fin: "20:00", activo: true },
    { dia: 4, inicio: "09:00", fin: "20:00", activo: true },
    { dia: 5, inicio: "09:00", fin: "20:00", activo: true },
    { dia: 6, inicio: "09:00", fin: "20:00", activo: false }
  ];
}

export default function AdminPeluqueros() {
  const [peluqueros, setPeluqueros] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPeluquero, setEditingPeluquero] = useState(null);
  
  // Form state
  const [formNombre, setFormNombre] = useState("");
  const [formTelefono, setFormTelefono] = useState("");
  const [formColor, setFormColor] = useState("#3A5A40");
  const [formActivo, setFormActivo] = useState(true);
  const [formHorarios, setFormHorarios] = useState(createDefaultHorarios());
  const [formDiasLibres, setFormDiasLibres] = useState([]);
  const [formEspecialidades, setFormEspecialidades] = useState("");
  const [formServiciosIds, setFormServiciosIds] = useState([]);
  
  // Modal para días libres
  const [showDiaLibreModal, setShowDiaLibreModal] = useState(false);
  const [nuevoDiaLibre, setNuevoDiaLibre] = useState({ fecha: "", motivo: "" });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [peluquerosRes, serviciosRes] = await Promise.all([
        getPeluqueros(),
        getServicios()
      ]);
      setPeluqueros(peluquerosRes.data);
      setServicios(serviciosRes.data.filter(s => s.activo));
    } catch (error) {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (peluquero = null) => {
    if (peluquero) {
      setEditingPeluquero(peluquero);
      setFormNombre(peluquero.nombre);
      setFormTelefono(peluquero.telefono || "");
      setFormColor(peluquero.color);
      setFormActivo(peluquero.activo);
      setFormHorarios(peluquero.horarios && peluquero.horarios.length > 0 ? peluquero.horarios : createDefaultHorarios());
      setFormDiasLibres(peluquero.dias_libres || []);
      setFormEspecialidades(peluquero.especialidades || "");
      setFormServiciosIds(peluquero.servicios_ids || []);
    } else {
      setEditingPeluquero(null);
      setFormNombre("");
      setFormTelefono("");
      setFormColor(COLORES[peluqueros.length % COLORES.length]);
      setFormActivo(true);
      setFormHorarios(createDefaultHorarios());
      setFormDiasLibres([]);
      setFormEspecialidades("");
      setFormServiciosIds([]);
    }
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!formNombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }

    const formData = {
      nombre: formNombre,
      telefono: formTelefono,
      color: formColor,
      activo: formActivo,
      horarios: formHorarios,
      dias_libres: formDiasLibres,
      especialidades: formEspecialidades,
      servicios_ids: formServiciosIds
    };

    try {
      if (editingPeluquero) {
        await updatePeluquero(editingPeluquero.id, formData);
        toast.success("Peluquero actualizado");
      } else {
        await createPeluquero(formData);
        toast.success("Peluquero creado");
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error("Error al guardar peluquero");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este peluquero? Se perderá todo su historial de citas.")) return;
    
    try {
      await deletePeluquero(id);
      toast.success("Peluquero eliminado");
      loadData();
    } catch (error) {
      toast.error("Error al eliminar peluquero");
    }
  };

  const updateHorario = (diaIndex, field, value) => {
    const newHorarios = formHorarios.map((h, i) => 
      i === diaIndex ? { ...h, [field]: value } : h
    );
    setFormHorarios(newHorarios);
  };

  const updateHorarioMulti = (diaIndex, updates) => {
    const newHorarios = formHorarios.map((h, i) => 
      i === diaIndex ? { ...h, ...updates } : h
    );
    setFormHorarios(newHorarios);
  };

  const handleAddDiaLibre = () => {
    if (!nuevoDiaLibre.fecha) {
      toast.error("Selecciona una fecha");
      return;
    }
    setFormDiasLibres([...formDiasLibres, nuevoDiaLibre]);
    setNuevoDiaLibre({ fecha: "", motivo: "" });
    setShowDiaLibreModal(false);
  };

  const handleRemoveDiaLibre = (index) => {
    setFormDiasLibres(formDiasLibres.filter((_, i) => i !== index));
  };

  const toggleServicio = (servicioId) => {
    if (formServiciosIds.includes(servicioId)) {
      setFormServiciosIds(formServiciosIds.filter(id => id !== servicioId));
    } else {
      setFormServiciosIds([...formServiciosIds, servicioId]);
    }
  };

  const getActiveDays = (horarios) => {
    if (!horarios || !Array.isArray(horarios)) return [];
    return horarios.filter(h => h.activo).map(h => DIAS[h.dia].slice(0, 3));
  };

  const getServiciosNombres = (serviciosIds) => {
    if (!serviciosIds || serviciosIds.length === 0) return "Todos los servicios";
    return serviciosIds.map(id => {
      const s = servicios.find(serv => serv.id === id);
      return s ? s.nombre : "";
    }).filter(Boolean).join(", ");
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="spinner w-10 h-10" />
      </div>
    );
  }

  return (
    <div data-testid="admin-peluqueros">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading">Peluqueros</h1>
          <p className="text-muted-foreground mt-1">Gestiona tu equipo, horarios y servicios</p>
        </div>
        <Button className="h-12 gap-2" onClick={() => handleOpenModal()} data-testid="btn-add-peluquero">
          <Plus className="h-4 w-4" />
          Nuevo peluquero
        </Button>
      </div>

      {peluqueros.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <UserCircle className="h-16 w-16 mx-auto text-muted-foreground mb-4" />
            <h3 className="text-xl font-heading mb-2">No hay peluqueros</h3>
            <p className="text-muted-foreground mb-4">Añade a los miembros de tu equipo</p>
            <Button onClick={() => handleOpenModal()}>
              <Plus className="h-4 w-4 mr-2" />
              Crear primer peluquero
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {peluqueros.map(peluquero => (
            <Card key={peluquero.id} className={!peluquero.activo ? "opacity-60" : ""}>
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center text-white text-xl font-bold"
                      style={{ backgroundColor: peluquero.color }}
                    >
                      {peluquero.nombre.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <h3 className="text-xl font-medium" data-testid={`peluquero-nombre-${peluquero.id}`}>
                        {peluquero.nombre}
                      </h3>
                      {!peluquero.activo && (
                        <Badge variant="secondary">Inactivo</Badge>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleOpenModal(peluquero)}
                      data-testid={`btn-edit-peluquero-${peluquero.id}`}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-destructive hover:text-destructive"
                      onClick={() => handleDelete(peluquero.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-3 pt-3 border-t">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                      <Clock className="h-3 w-3" />
                      Días que trabaja
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {getActiveDays(peluquero.horarios).map(day => (
                        <Badge key={day} variant="outline" className="text-xs">
                          {day}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div>
                    <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
                      <Scissors className="h-3 w-3" />
                      Servicios
                    </p>
                    <p className="text-sm">
                      {getServiciosNombres(peluquero.servicios_ids)}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Modal editar/crear peluquero */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingPeluquero ? `Editar: ${editingPeluquero.nombre}` : "Nuevo peluquero"}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="datos" className="w-full">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="datos">Datos</TabsTrigger>
              <TabsTrigger value="horario">Horario</TabsTrigger>
              <TabsTrigger value="servicios">Servicios</TabsTrigger>
              <TabsTrigger value="libres">Días libres</TabsTrigger>
            </TabsList>

            <TabsContent value="datos" className="space-y-4 mt-4">
              <div>
                <Label>Nombre *</Label>
                <Input
                  placeholder="Ej: Carlos"
                  value={formNombre}
                  onChange={e => setFormNombre(e.target.value)}
                  className="h-12"
                  data-testid="modal-input-nombre"
                />
              </div>
              <div>
                <Label>Teléfono (opcional)</Label>
                <Input
                  placeholder="612345678"
                  value={formTelefono}
                  onChange={e => setFormTelefono(e.target.value)}
                  className="h-12"
                />
              </div>
              <div>
                <Label>Color en calendario</Label>
                <div className="flex gap-2 mt-2">
                  {COLORES.map(color => (
                    <button
                      key={color}
                      type="button"
                      className={`w-10 h-10 rounded-full transition-all ${
                        formColor === color ? "ring-2 ring-offset-2 ring-primary scale-110" : "hover:scale-105"
                      }`}
                      style={{ backgroundColor: color }}
                      onClick={() => setFormColor(color)}
                    />
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between pt-4 border-t">
                <div>
                  <Label>Peluquero activo</Label>
                  <p className="text-xs text-muted-foreground">Si está inactivo, no aparecerá en las reservas</p>
                </div>
                <Switch
                  checked={formActivo}
                  onCheckedChange={setFormActivo}
                  data-testid="modal-switch-activo"
                />
              </div>
            </TabsContent>

            <TabsContent value="horario" className="mt-4">
              <CardDescription className="mb-4">
                Define el horario semanal de este peluquero
              </CardDescription>
              <div className="space-y-3">
                {formHorarios.map((horario, idx) => (
                  <div key={idx} className="py-3 border-b last:border-0">
                    <div className="flex items-center gap-3">
                      <Switch
                        checked={horario.activo}
                        onCheckedChange={v => updateHorario(idx, "activo", v)}
                      />
                      <span className="w-24 font-medium text-sm">{DIAS[idx]}</span>
                      {horario.activo ? (
                        <div className="flex flex-col gap-2 flex-1">
                          <div className="flex items-center gap-2">
                            <Input
                              type="time"
                              value={horario.inicio}
                              onChange={e => updateHorario(idx, "inicio", e.target.value)}
                              className="w-28 h-10"
                            />
                            <span className="text-muted-foreground">a</span>
                            <Input
                              type="time"
                              value={horario.fin}
                              onChange={e => updateHorario(idx, "fin", e.target.value)}
                              className="w-28 h-10"
                            />
                            {!horario.inicio2 && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs text-muted-foreground"
                                onClick={() => updateHorarioMulti(idx, { inicio2: "16:00", fin2: "20:00" })}
                              >
                                + Tarde
                              </Button>
                            )}
                          </div>
                          {horario.inicio2 && (
                            <div className="flex items-center gap-2">
                              <Input
                                type="time"
                                value={horario.inicio2}
                                onChange={e => updateHorario(idx, "inicio2", e.target.value)}
                                className="w-28 h-10"
                              />
                              <span className="text-muted-foreground">a</span>
                              <Input
                                type="time"
                                value={horario.fin2}
                                onChange={e => updateHorario(idx, "fin2", e.target.value)}
                                className="w-28 h-10"
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="text-destructive hover:text-destructive h-8 w-8"
                                onClick={() => updateHorarioMulti(idx, { inicio2: null, fin2: null })}
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      ) : (
                        <Badge variant="secondary">No trabaja</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </TabsContent>

            <TabsContent value="servicios" className="mt-4">
              <CardDescription className="mb-4">
                Selecciona los servicios que ofrece este peluquero. Si no seleccionas ninguno, ofrecerá todos.
              </CardDescription>
              <div className="space-y-3">
                {servicios.map(servicio => (
                  <div
                    key={servicio.id}
                    className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                      formServiciosIds.includes(servicio.id) ? "bg-accent/10 border-accent" : "hover:bg-muted/50"
                    }`}
                    onClick={() => toggleServicio(servicio.id)}
                  >
                    <Checkbox
                      checked={formServiciosIds.includes(servicio.id)}
                      onCheckedChange={() => toggleServicio(servicio.id)}
                    />
                    <div className="flex-1">
                      <p className="font-medium">{servicio.nombre}</p>
                      <p className="text-sm text-muted-foreground">
                        {servicio.duracion_minutos} min - {servicio.precio}€
                      </p>
                    </div>
                  </div>
                ))}
              </div>
              {formServiciosIds.length === 0 && (
                <p className="text-sm text-muted-foreground mt-4 p-3 bg-muted/50 rounded">
                  ℹ️ No has seleccionado ningún servicio, por lo que este peluquero ofrecerá todos los servicios disponibles.
                </p>
              )}
            </TabsContent>

            <TabsContent value="libres" className="mt-4">
              <div className="flex items-center justify-between mb-4">
                <CardDescription>
                  Días específicos que este peluquero no trabaja (vacaciones, permisos...)
                </CardDescription>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDiaLibreModal(true)}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Añadir día
                </Button>
              </div>

              {formDiasLibres.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                  <p>No hay días libres programados</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {formDiasLibres.map((dia, idx) => (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-md"
                    >
                      <div>
                        <span className="font-medium">
                          {format(new Date(dia.fecha), "EEEE d 'de' MMMM yyyy", { locale: es })}
                        </span>
                        {dia.motivo && (
                          <span className="text-muted-foreground ml-2">- {dia.motivo}</span>
                        )}
                      </div>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={() => handleRemoveDiaLibre(idx)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} data-testid="modal-btn-guardar">
              {editingPeluquero ? "Guardar cambios" : "Crear peluquero"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal añadir día libre */}
      <Dialog open={showDiaLibreModal} onOpenChange={setShowDiaLibreModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Añadir día libre</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Fecha</Label>
              <Input
                type="date"
                value={nuevoDiaLibre.fecha}
                onChange={e => setNuevoDiaLibre({ ...nuevoDiaLibre, fecha: e.target.value })}
                className="h-12"
              />
            </div>
            <div>
              <Label>Motivo (opcional)</Label>
              <Input
                placeholder="Ej: Vacaciones, Permiso personal..."
                value={nuevoDiaLibre.motivo}
                onChange={e => setNuevoDiaLibre({ ...nuevoDiaLibre, motivo: e.target.value })}
                className="h-12"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDiaLibreModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddDiaLibre}>
              Añadir día libre
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
